import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Calendar, MapPin, Users, Upload, ArrowLeft, Plus, X, Camera, File as FileIcon, Mail, Phone } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'

const EventDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAddCardModal, setShowAddCardModal] = useState(false)
  const [cardFile, setCardFile] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [fileType, setFileType] = useState(null)
  const [captureMode, setCaptureMode] = useState('upload') // 'upload' or 'camera'
  const [showWebcam, setShowWebcam] = useState(false)
  const [stream, setStream] = useState(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const [manualData, setManualData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    designation: ''
  })

  useEffect(() => {
    fetchEventDetails()
  }, [id])

  const fetchEventDetails = async () => {
    try {
      const response = await api.get(`/events/${id}`)
      setEvent(response.data)
    } catch (error) {
      console.error('Failed to fetch event:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setCardFile(file)
    const type = file.type
    setFileType(type)

    // Create preview
    if (type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setFilePreview(reader.result)
      }
      reader.readAsDataURL(file)
    } else if (type === 'application/pdf') {
      setFilePreview(URL.createObjectURL(file))
    }
  }

  const handleCameraCapture = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setCardFile(file)
    setFileType(file.type)

    const reader = new FileReader()
    reader.onloadend = () => {
      setFilePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const clearFile = () => {
    setCardFile(null)
    setFilePreview(null)
    setFileType(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (cameraInputRef.current) cameraInputRef.current.value = ''
  }

  const startWebcam = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
      })
      setStream(mediaStream)
      setShowWebcam(true)
      
      // Wait for next tick to ensure video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream
          videoRef.current.play().catch(err => {
            console.error('Error playing video:', err)
          })
        }
      }, 100)
    } catch (error) {
      console.error('Error accessing webcam:', error)
      toast.error('Unable to access camera. Please check permissions or use file upload.')
    }
  }

  const stopWebcam = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setShowWebcam(false)
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error('Camera not ready. Please try again.')
      return
    }

    const video = videoRef.current
    const canvas = canvasRef.current

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth || 640
    canvas.height = video.videoHeight || 480

    if (canvas.width === 0 || canvas.height === 0) {
      toast.error('Video stream not ready. Please wait a moment and try again.')
      return
    }

    // Draw the video frame to canvas
    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Failed to capture photo. Please try again.')
        return
      }

      const file = new File([blob], `business-card-${Date.now()}.jpg`, { type: 'image/jpeg' })
      setCardFile(file)
      setFileType('image/jpeg')
      
      const reader = new FileReader()
      reader.onloadend = () => {
        setFilePreview(reader.result)
      }
      reader.readAsDataURL(file)
      
      stopWebcam()
    }, 'image/jpeg', 0.95)
  }

  // Cleanup webcam on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
    }
  }, [stream])

  const handleAddAttendee = async (e) => {
    e.preventDefault()
    try {
      const formData = new FormData()
      formData.append('name', manualData.name)
      formData.append('email', manualData.email)
      formData.append('phone', manualData.phone)
      formData.append('company', manualData.company)
      formData.append('designation', manualData.designation)
      
      if (cardFile) {
        formData.append('businessCard', cardFile)
      }

      await api.post(`/events/${id}/attendees`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      setShowAddCardModal(false)
      setManualData({
        name: '',
        email: '',
        phone: '',
        company: '',
        designation: ''
      })
      clearFile()
      toast.success('Attendee added successfully!')
      fetchEventDetails()
    } catch (error) {
      console.error('Failed to add attendee:', error)
      toast.error(error.response?.data?.error || 'Failed to add attendee. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!event) {
    return <div className="text-center py-12">Event not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/events')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{event.name}</h1>
          <p className="text-gray-600 mt-1">{event.type} Event</p>
        </div>
        <button
          onClick={() => setShowAddCardModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Attendee
        </button>
      </div>

      {/* Event Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-semibold">{format(new Date(event.date), 'MMM dd, yyyy')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-semibold">{event.location || 'TBD'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Attendees</p>
              <p className="font-semibold">{event.totalContacts || 0} contacts</p>
            </div>
          </div>
        </div>

        {event.description && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Description</p>
            <p className="text-gray-600">{event.description}</p>
          </div>
        )}

        {/* Team Members */}
        {event.teamMembers && event.teamMembers.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Team Attending</p>
            <div className="flex items-center gap-2">
              {event.teamMembers.slice(0, 1).map((member) => (
                <div key={member._id} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium">
                  <span>{member.name}</span>
                  {event.teamMembers.length > 1 && (
                    <span className="relative group cursor-pointer">
                      <span className="px-2 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                        +{event.teamMembers.length - 1}
                      </span>
                      <div className="hidden group-hover:block absolute top-full left-0 mt-1 bg-gray-900 text-white text-xs rounded-lg p-2 whitespace-nowrap z-10">
                        {event.teamMembers.slice(1).map((m, i) => (
                          <div key={i} className="py-1">{m.name}</div>
                        ))}
                      </div>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Attendees List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Attendees</h2>
          <span className="text-sm text-gray-600">{event.attendees?.length || 0} contacts</span>
        </div>

        {event.attendees && event.attendees.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {event.attendees.map((attendee, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{attendee.name || 'No name'}</h4>
                    {attendee.designation && (
                      <p className="text-sm text-gray-600">{attendee.designation}</p>
                    )}
                    {attendee.company && (
                      <p className="text-sm text-gray-500">{attendee.company}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    attendee.followupStatus === 'Converted' 
                      ? 'bg-green-100 text-green-700'
                      : attendee.followupStatus === 'Contacted'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {attendee.followupStatus || 'Pending'}
                  </span>
                </div>

                <div className="space-y-2 mb-3">
                  {attendee.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${attendee.email}`} className="hover:text-primary-600 truncate">
                        {attendee.email}
                      </a>
                    </div>
                  )}
                  {attendee.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${attendee.phone}`} className="hover:text-primary-600">
                        {attendee.phone}
                      </a>
                    </div>
                  )}
                </div>

                {(attendee.businessCardPath || attendee.businessCardType) && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Business Card Uploaded </p>
                    {attendee.businessCardType?.startsWith('image/') && attendee.businessCardPath && (
                      <div className="text-xs text-blue-600 flex items-center gap-1">
                        <FileIcon className="w-3 h-3" />
                        Image file saved
                      </div>
                    )}
                    {attendee.businessCardType === 'application/pdf' && (
                      <div className="text-xs text-red-600 flex items-center gap-1">
                        <FileIcon className="w-3 h-3" />
                        PDF file saved
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No attendees yet. Add business cards to get started.</p>
          </div>
        )}
      </div>

      {/* Add Attendee Modal */}
      {showAddCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add Attendee</h3>
              <button
                onClick={() => {
                  stopWebcam()
                  setShowAddCardModal(false)
                  setCaptureMode('upload')
                  clearFile()
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddAttendee} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Card (Optional)
                </label>
                
                {/* Capture Mode Selector */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      stopWebcam()
                      setCaptureMode('upload')
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                      captureMode === 'upload'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      stopWebcam()
                      setCaptureMode('camera')
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                      captureMode === 'camera'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Camera className="w-4 h-4" />
                    Use Camera
                  </button>
                </div>

                {/* File Upload */}
                {captureMode === 'upload' && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Supported: Images (JPG, PNG, etc.) and PDF</p>
                  </div>
                )}

                {/* Camera Capture */}
                {captureMode === 'camera' && (
                  <div className="space-y-3">
                    {!showWebcam && !filePreview && (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={startWebcam}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Camera className="w-5 h-5" />
                          Open Camera
                        </button>
                        <p className="text-xs text-gray-500 text-center">Works on desktop and mobile devices</p>
                        
                        {/* Fallback for mobile file input */}
                        <div className="relative">
                          <div className="text-center text-sm text-gray-500 mb-2">or</div>
                          <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleCameraCapture}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                          />
                          <p className="text-xs text-gray-500 mt-1">📱 Mobile: Use device camera</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Webcam View */}
                    {showWebcam && (
                      <div className="space-y-3">
                        <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ minHeight: '320px' }}>
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-80 object-cover"
                          />
                          <div className="absolute bottom-2 left-2 text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
                            📹 Camera Active
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={capturePhoto}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Camera className="w-4 h-4" />
                            Capture Photo
                          </button>
                          <button
                            type="button"
                            onClick={stopWebcam}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* File Preview */}
                {filePreview && (
                  <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {fileType?.startsWith('image/') ? (
                          <FileIcon className="w-4 h-4 text-blue-600" />
                        ) : (
                          <FileIcon className="w-4 h-4 text-red-600" />
                        )}
                        <span className="text-sm font-medium text-gray-700">
                          {cardFile?.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={clearFile}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Image Preview */}
                    {fileType?.startsWith('image/') && (
                      <img
                        src={filePreview}
                        alt="Business card preview"
                        className="w-full h-48 object-contain rounded border border-gray-300 bg-white"
                      />
                    )}
                    
                    {/* PDF Preview */}
                    {fileType === 'application/pdf' && (
                      <div className="w-full h-48 flex items-center justify-center bg-white border border-gray-300 rounded">
                        <div className="text-center">
                          <FileIcon className="w-12 h-12 text-red-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">PDF Document</p>
                          <a
                            href={filePreview}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:underline"
                          >
                            Open in new tab
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Contact Details {filePreview ? '(Optional - AI will extract from card)' : '*'}</p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Name *"
                    value={manualData.name}
                    onChange={(e) => setManualData({...manualData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={manualData.email}
                    onChange={(e) => setManualData({...manualData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={manualData.phone}
                    onChange={(e) => setManualData({...manualData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={manualData.company}
                    onChange={(e) => setManualData({...manualData, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Designation"
                    value={manualData.designation}
                    onChange={(e) => setManualData({...manualData, designation: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddCardModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Add Attendee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventDetails
